import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Check } from "lucide-react";
import { toast } from "sonner";

import { TIERS, AMENITY_OPTIONS, PROPERTY_TYPES } from "@/lib/tiers";
import TierCard from "@/components/TierCard";
import StepIndicator from "@/components/StepIndicator";

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STEPS = [
  "Service level",
  "About you",
  "Your property",
  "Availability",
  "Amenities",
  "Review",
];

const stepVariants = {
  enter: { opacity: 0, y: 16 },
  center: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -16 },
};

const emptyForm = {
  tier: "",
  full_name: "",
  email: "",
  phone: "",
  property_name: "",
  property_type: "",
  address: "",
  bedrooms: 1,
  bathrooms: 1,
  max_guests: 2,
  available_from: "",
  nightly_rate: "",
  min_stay_nights: 1,
  amenities: [],
  notes: "",
};

export default function Onboard() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    ...emptyForm,
    tier: searchParams.get("tier") || "",
  });

  useEffect(() => {
    const t = searchParams.get("tier");
    if (t && ["basic", "standard", "premium"].includes(t)) {
      setForm((f) => ({ ...f, tier: t }));
    }
  }, [searchParams]);

  const update = (patch) => setForm((f) => ({ ...f, ...patch }));

  const stepIsValid = useMemo(() => {
    switch (step) {
      case 0:
        return !!form.tier;
      case 1:
        return (
          form.full_name.trim().length > 1 &&
          /\S+@\S+\.\S+/.test(form.email) &&
          form.phone.trim().length >= 4
        );
      case 2:
        return (
          form.property_name.trim().length >= 1 &&
          !!form.property_type &&
          form.address.trim().length >= 4 &&
          Number(form.bedrooms) >= 1 &&
          Number(form.bathrooms) >= 1
        );
      case 3:
        return (
          Number(form.max_guests) >= 1 &&
          Number(form.min_stay_nights) >= 1 &&
          !!form.available_from &&
          form.nightly_rate !== "" &&
          Number(form.nightly_rate) > 0
        );
      case 4:
        return form.amenities.length >= 1 && form.notes.trim().length >= 1;
      case 5:
        return true;
      default:
        return false;
    }
  }, [step, form]);

  const next = () => {
    if (!stepIsValid) {
      toast.error("Please complete the highlighted fields to continue.");
      return;
    }
    setStep((s) => Math.min(s + 1, STEPS.length - 1));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const back = () => {
    setStep((s) => Math.max(s - 1, 0));
    if (typeof window !== "undefined") window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        bedrooms: Number(form.bedrooms) || 1,
        bathrooms: Number(form.bathrooms) || 1,
        max_guests: Number(form.max_guests) || 1,
        min_stay_nights: Number(form.min_stay_nights) || 1,
        nightly_rate: Number(form.nightly_rate),
      };
      const res = await axios.post(`${API}/submissions`, payload);
      toast.success("Your application has been received.");
      navigate(`/success?id=${res.data.id}`);
    } catch (err) {
      console.error(err);
      const msg =
        err?.response?.data?.detail?.[0]?.msg ||
        err?.response?.data?.detail ||
        "Something went quietly wrong. Please try again.";
      toast.error(typeof msg === "string" ? msg : "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="onboard-page" className="bg-[#FAF9F6] min-h-screen">
      <div className="max-w-6xl mx-auto px-6 md:px-12 py-12 lg:py-20">
        <div className="eyebrow" data-testid="onboard-eyebrow">
          New host application
        </div>
        <h1 className="font-serif-display text-4xl lg:text-5xl mt-4 text-[#2A1F1A] leading-tight">
          Let&apos;s begin.
        </h1>
        <p className="mt-4 text-[#6E6E70] max-w-lg leading-relaxed">
          A few unhurried steps. Save your progress at any time — your answers
          stay on this device until you choose to submit.
        </p>

        <div className="mt-12">
          <StepIndicator steps={STEPS} current={step} />
        </div>

        <div className="mt-12 bg-white border border-[#E3DED5] p-6 md:p-12 lg:p-16 min-h-[520px]">
          <AnimatePresence mode="wait">
            <motion.div
              key={step}
              variants={stepVariants}
              initial="enter"
              animate="center"
              exit="exit"
              transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            >
              {step === 0 && (
                <StepTier form={form} update={update} />
              )}
              {step === 1 && <StepHostInfo form={form} update={update} />}
              {step === 2 && <StepProperty form={form} update={update} />}
              {step === 3 && <StepAvailability form={form} update={update} />}
              {step === 4 && <StepAmenities form={form} update={update} />}
              {step === 5 && <StepReview form={form} />}
            </motion.div>
          </AnimatePresence>
        </div>

        <div className="mt-8 flex items-center justify-between">
          <button
            type="button"
            onClick={back}
            disabled={step === 0}
            data-testid="btn-prev-step"
            className="btn-secondary inline-flex items-center gap-2 px-5 py-3 text-sm tracking-wide rounded-none disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ArrowLeft size={16} /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              onClick={next}
              data-testid="btn-next-step"
              className="btn-primary inline-flex items-center gap-2 px-6 py-3 text-sm tracking-wide rounded-none"
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              onClick={submit}
              disabled={submitting}
              data-testid="btn-submit-application"
              className="btn-primary inline-flex items-center gap-2 px-7 py-3 text-sm tracking-wide rounded-none"
            >
              {submitting ? "Submitting…" : "Submit application"}
              <ArrowRight size={16} />
            </button>
          )}
        </div>
      </div>
    </main>
  );
}

/* -------------------- Step 0 -------------------- */
function StepTier({ form, update }) {
  return (
    <div>
      <div className="eyebrow">Step 01</div>
      <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 text-[#2A1F1A]">
        Choose your hosting option
      </h2>
      <p className="mt-4 text-[#6E6E70] max-w-xl">
        Pick the partnership that fits where you are right now. Don&apos;t worry —
        you can change this at any time during onboarding.
      </p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-6 items-stretch">
        {TIERS.map((t) => (
          <TierCard
            key={t.id}
            tier={t}
            selected={form.tier === t.id}
            onSelect={(id) => update({ tier: id })}
            testIdPrefix="onboard-tier"
          />
        ))}
      </div>
    </div>
  );
}

/* -------------------- Step 1 -------------------- */
function StepHostInfo({ form, update }) {
  return (
    <div>
      <div className="eyebrow">Step 02</div>
      <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 text-[#2A1F1A]">
        About you
      </h2>
      <p className="mt-4 text-[#6E6E70] max-w-xl">
        How should we address you and where can we reach you?
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 max-w-3xl">
        <Field label="Full name" required>
          <Input
            data-testid="input-full-name"
            value={form.full_name}
            onChange={(e) => update({ full_name: e.target.value })}
            placeholder="Inês Martins"
            className="editorial-input"
          />
        </Field>

        <Field label="Email" required>
          <Input
            data-testid="input-email"
            type="email"
            value={form.email}
            onChange={(e) => update({ email: e.target.value })}
            placeholder="you@example.com"
            className="editorial-input"
          />
        </Field>

        <Field label="Phone" required>
          <Input
            data-testid="input-phone"
            value={form.phone}
            onChange={(e) => update({ phone: e.target.value })}
            placeholder="+1 555 000 0000"
            className="editorial-input"
          />
        </Field>
      </div>
    </div>
  );
}

/* -------------------- Step 2 -------------------- */
function StepProperty({ form, update }) {
  return (
    <div>
      <div className="eyebrow">Step 03</div>
      <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 text-[#2A1F1A]">
        Your property
      </h2>
      <p className="mt-4 text-[#6E6E70] max-w-xl">
        Tell us a little about the home you&apos;d like us to host.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 max-w-3xl">
        <Field label="Property name" required>
          <Input
            data-testid="input-property-name"
            value={form.property_name}
            onChange={(e) => update({ property_name: e.target.value })}
            placeholder="Casa da Lua"
            className="editorial-input"
          />
        </Field>

        <Field label="Property type" required>
          <Select
            value={form.property_type}
            onValueChange={(v) => update({ property_type: v })}
          >
            <SelectTrigger
              data-testid="select-property-type"
              className="editorial-input justify-between"
            >
              <SelectValue placeholder="Choose a type" />
            </SelectTrigger>
            <SelectContent>
              {PROPERTY_TYPES.map((t) => (
                <SelectItem
                  key={t}
                  value={t}
                  data-testid={`option-property-type-${t.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {t}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </Field>

        <div className="md:col-span-2">
          <Field label="Address" required>
            <Input
              data-testid="input-address"
              value={form.address}
              onChange={(e) => update({ address: e.target.value })}
              placeholder="Street, city, country"
              className="editorial-input"
            />
          </Field>
        </div>

        <Field label="Bedrooms" required>
          <Input
            data-testid="input-bedrooms"
            type="number"
            min={1}
            value={form.bedrooms}
            onChange={(e) => update({ bedrooms: e.target.value })}
            className="editorial-input"
          />
        </Field>

        <Field label="Bathrooms" required>
          <Input
            data-testid="input-bathrooms"
            type="number"
            min={1}
            value={form.bathrooms}
            onChange={(e) => update({ bathrooms: e.target.value })}
            className="editorial-input"
          />
        </Field>
      </div>
    </div>
  );
}

/* -------------------- Step 3 -------------------- */
function StepAvailability({ form, update }) {
  return (
    <div>
      <div className="eyebrow">Step 04</div>
      <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 text-[#2A1F1A]">
        Availability & pricing
      </h2>
      <p className="mt-4 text-[#6E6E70] max-w-xl">
        These help us plan around your calendar and shape a fair, considered
        nightly rate.
      </p>

      <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-x-10 gap-y-8 max-w-3xl">
        <Field label="Maximum guests" required>
          <Input
            data-testid="input-max-guests"
            type="number"
            min={1}
            value={form.max_guests}
            onChange={(e) => update({ max_guests: e.target.value })}
            className="editorial-input"
          />
        </Field>

        <Field label="Minimum stay (nights)" required>
          <Input
            data-testid="input-min-stay"
            type="number"
            min={1}
            value={form.min_stay_nights}
            onChange={(e) => update({ min_stay_nights: e.target.value })}
            className="editorial-input"
          />
        </Field>

        <Field label="Available from" required>
          <Input
            data-testid="input-available-from"
            type="date"
            value={form.available_from}
            onChange={(e) => update({ available_from: e.target.value })}
            className="editorial-input"
          />
        </Field>

        <Field label="Indicative nightly rate ($)" required>
          <Input
            data-testid="input-nightly-rate"
            type="number"
            min={1}
            step="1"
            value={form.nightly_rate}
            onChange={(e) => update({ nightly_rate: e.target.value })}
            placeholder="180"
            className="editorial-input"
          />
        </Field>
      </div>
    </div>
  );
}

/* -------------------- Step 4 -------------------- */
function StepAmenities({ form, update }) {
  const toggle = (a) => {
    const exists = form.amenities.includes(a);
    update({
      amenities: exists
        ? form.amenities.filter((x) => x !== a)
        : [...form.amenities, a],
    });
  };

  return (
    <div>
      <div className="eyebrow">Step 05</div>
      <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 text-[#2A1F1A]">
        Amenities & notes
      </h2>
      <p className="mt-4 text-[#6E6E70] max-w-xl">
        Select what your home offers. We&apos;ll use this to shape the listing
        narrative and welcome guide.
      </p>

      <div className="mt-10 grid grid-cols-2 md:grid-cols-3 gap-4 max-w-3xl">
        {AMENITY_OPTIONS.map((a) => {
          const checked = form.amenities.includes(a);
          return (
            <label
              key={a}
              data-testid={`amenity-${a.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className={`flex items-center gap-3 border px-4 py-3 cursor-pointer transition-colors ${
                checked
                  ? "border-[#C03028] bg-[#F2EFE9]"
                  : "border-[#E3DED5] hover:border-[#B0895C]"
              }`}
            >
              <Checkbox
                checked={checked}
                onCheckedChange={() => toggle(a)}
                className="data-[state=checked]:bg-[#C03028] data-[state=checked]:border-[#C03028] rounded-none"
              />
              <span className="text-sm text-[#2A1F1A]">{a}</span>
            </label>
          );
        })}
      </div>

      <div className="mt-10 max-w-3xl">
        <Field label="Anything we should know?" required>
          <Textarea
            data-testid="textarea-notes"
            value={form.notes}
            onChange={(e) => update({ notes: e.target.value })}
            rows={4}
            placeholder="Quirks, preferences, or anything you'd like us to be mindful of."
            className="bg-transparent border border-[#E3DED5] focus-visible:ring-0 focus-visible:border-[#C03028] rounded-none shadow-none p-4 font-sans-body"
          />
        </Field>
      </div>
    </div>
  );
}

/* -------------------- Step 5 -------------------- */
function StepReview({ form }) {
  const tier = TIERS.find((t) => t.id === form.tier);
  return (
    <div data-testid="review-step">
      <div className="eyebrow">Step 06</div>
      <h2 className="font-serif-display text-3xl lg:text-4xl mt-3 text-[#2A1F1A]">
        A quiet moment to review
      </h2>
      <p className="mt-4 text-[#6E6E70] max-w-xl">
        Please confirm everything reads correctly, then submit your
        application.
      </p>

      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        <ReviewBlock title="Service level">
          {tier ? (
            <>
              <div className="font-serif-display text-2xl text-[#2A1F1A]">
                {tier.name}
              </div>
              <div className="text-sm text-[#6E6E70] mt-1">
                {tier.commission} commission
              </div>
              <div className="text-sm text-[#6E6E70] mt-3 leading-relaxed">
                {tier.tagline}
              </div>
            </>
          ) : (
            <em className="text-[#6E6E70]">Not selected</em>
          )}
        </ReviewBlock>

        <ReviewBlock title="Host">
          <ReviewLine k="Name" v={form.full_name} />
          <ReviewLine k="Email" v={form.email} />
          <ReviewLine k="Phone" v={form.phone || "—"} />
        </ReviewBlock>

        <ReviewBlock title="Property">
          <ReviewLine k="Name" v={form.property_name} />
          <ReviewLine k="Type" v={form.property_type} />
          <ReviewLine k="Address" v={form.address} />
          <ReviewLine
            k="Layout"
            v={`${form.bedrooms} bed · ${form.bathrooms} bath · ${form.max_guests} guests`}
          />
        </ReviewBlock>

        <ReviewBlock title="Availability">
          <ReviewLine k="From" v={form.available_from || "Flexible"} />
          <ReviewLine
            k="Minimum stay"
            v={`${form.min_stay_nights} night${form.min_stay_nights > 1 ? "s" : ""}`}
          />
          <ReviewLine
            k="Indicative rate"
            v={form.nightly_rate ? `$${form.nightly_rate} / night` : "To be set together"}
          />
        </ReviewBlock>

        <ReviewBlock title="Amenities">
          {form.amenities.length === 0 ? (
            <em className="text-[#6E6E70]">None selected</em>
          ) : (
            <ul className="space-y-2">
              {form.amenities.map((a) => (
                <li key={a} className="flex items-center gap-2 text-sm text-[#2A1F1A]">
                  <Check size={14} className="text-[#B0895C]" /> {a}
                </li>
              ))}
            </ul>
          )}
        </ReviewBlock>

        <ReviewBlock title="Notes">
          <p className="text-sm text-[#6E6E70] leading-relaxed">
            {form.notes || "—"}
          </p>
        </ReviewBlock>
      </div>
    </div>
  );
}

/* -------------------- helpers -------------------- */
function Field({ label, required, children }) {
  return (
    <div>
      <Label className="eyebrow block">
        {label} {required && <span className="text-[#C03028]">*</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}

function ReviewBlock({ title, children }) {
  return (
    <div className="border border-[#E3DED5] p-6 bg-[#FAF9F6]">
      <div className="eyebrow">{title}</div>
      <div className="mt-4 space-y-1.5">{children}</div>
    </div>
  );
}

function ReviewLine({ k, v }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-sm">
      <span className="text-[#6E6E70]">{k}</span>
      <span className="text-[#2A1F1A] text-right truncate max-w-[60%]">
        {v || "—"}
      </span>
    </div>
  );
}
